<template>
  <div ref="graphContainer" class="relative w-full h-full">
    <!-- Loading -->
    <transition>
      <div v-if="loading" class="absolute top-32 left-1/2 transform -translate-x-1/2 z-50">
        <BaseLoadingSpinnerCircle :width="32" :height="32" :duration="1" class="text-text-normal" />
      </div>
    </transition>
    <!-- No Rank Data Available (the ad list below carries the full explanation, so keep this compact) -->
    <transition>
      <div v-if="noData" class="absolute inset-0 z-40 flex flex-col items-center justify-center pb-6 pointer-events-none">
        <TrophyIcon class="text-icon-normal" />
        <BaseText type="label" size="sm" class="text-text-normal mt-3">
          No Rank Data Available
        </BaseText>
      </div>
    </transition>
    <!-- Color Reference Key -->
    <!-- <div class="absolute top-0 right-6 flex items-center gap-3">
      <div v-for="colorRef in colorReferences" :key="`color-reference-${colorRef.label}`" class="flex items-center gap-0.5 p-0.5">
        <div class="w-4 h-4 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
            <circle cx="3" cy="3" r="3" :fill="colorRef.color" />
          </svg>
        </div>
        <BaseText type="body" size="xs" class="text-text-normal">
          {{ colorRef.label }}
        </BaseText>
      </div>
    </div> -->
    <div class="w-full h-full flex gap-3">
      <!-- Y-Axis Labels -->
      <div class="h-full flex flex-col pl-3.5 transition-opacity duration-100" style="width: 48px;"
      :class="{ 'opacity-0': noData }">
        <BaseText type="label" size="xs" class="text-text-normal flex-shrink-0" style="height: 26px;">
          Rank
        </BaseText>
        <div class="flex-grow flex flex-col justify-between" style="padding-bottom: 50px">
          <BaseText v-for="rank in rankLabels" :key="`rank-label-${rank}`" type="body" size="xs" class="text-text-subdued">
            {{ rank }}
          </BaseText>
        </div>
      </div>
      <div class="flex-grow flex flex-col pt-8 pr-6">
        <!-- Graph Area -->
        <div class="relative w-full" style="height: 242px;">
          <div class="scatter-background" />
          <div ref="dataArea" class="absolute top-0 bottom-0 left-2 right-2">
            <DataRenderer 
              ref="dataRenderer"
              :data="graphData"
              :axis-scales="axisScales"
              uid-key="id"
              x-axis-value-key="days_running"
              y-axis-value-key="rank"
              :data-area-dimensions="dataAreaDimensions"
              enable-details-load-handler
              :loading-point-details-id.sync="loadingPointDetailsId"
              :preview-width="200"
              @point-click="fetchPointAdvertisement"
            >
              <template #preview-footer="{ point, type }">
                <div v-if="point?.data" class="w-full flex flex-col" :class="{'pt-1.5': type !== 'video'}">
                  <!-- Category Label -->
                  <div v-if="point?.data?.category" class="w-full flex items-center justify-center p-1 rounded-md mb-1"
                  :style="{ 
                    backgroundColor: getCategoryLabel(point?.data?.category)?.color?.background,
                    color: getCategoryLabel(point?.data?.category)?.color?.text
                  }">
                    <component :is="getCategoryLabel(point?.data?.category)?.icon" :style="{ color: getCategoryLabel(point?.data?.category)?.color?.icon }" />
                    <BaseText type="label" size="xs" class="px-1" :style="{ color: getCategoryLabel(point?.data?.category)?.color?.text }">
                      {{ getCategoryLabel(point?.data?.category)?.label }}
                    </BaseText>
                  </div>
                  <!-- Rank Labels -->
                  <div class="w-full flex items-center justify-center">
                    <AdRankIcon class="text-neutral-alpha-650" />
                    <div class="flex items-center py-1 rounded-md">
                      <BaseText v-if="point?.data?.previous_rank" type="body" size="sm" class="px-1 text-neutral-alpha-500">
                        {{ point?.data?.previous_rank ?? '--' }}
                      </BaseText>
                      <ArrowIcon v-if="point?.data?.previous_rank" class="text-neutral-alpha-500" />
                      <BaseText type="label" size="sm" class="px-1 text-white">
                        {{ point?.data?.rank }}
                      </BaseText>
                    </div>
                  </div>
                </div>
              </template>
            </DataRenderer>
          </div>
        </div>
        <!-- Days Live Labels -->
        <div class="w-full flex justify-between pt-4 px-2 h-7">
          <div v-for="label in daysLiveLabels" :key="`days-live-label-${label}`" class="relative w-0 h-4 fade-in">
            <BaseText type="body" size="xs" class="text-text-subdued absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
              {{ label }}
            </BaseText>
          </div>
        </div>
        <div class="w-full flex justify-center pt-3 transition-opacity duration-100" :class="{ 'opacity-0': noData }">
          <BaseText type="label" size="xs" class="text-text-normal">
            Time Active
          </BaseText>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import { scaleLinear } from 'd3-scale'
import DataRenderer from '@/components/graphs/scatter/DataRenderer.vue'

// Icons
import AdRankIcon from '@/components/globals/Icons/SpyderModeIcons/AdRankIcon.vue'
import ArrowIcon from '@/components/globals/Icons/ArrowIcon.vue'
import BreakoutIcon from '@/components/globals/Icons/SpyderV2Icons/BreakoutIcon.vue'
import EvergreenIcon from '@/components/globals/Icons/SpyderV2Icons/EvergreenIcon.vue'
import TrophyIcon from '@/components/globals/Icons/TrophyIcon.vue'

const MIN_X_TICK_SPACING = 56 // px
const CATEGORY_COLORS = { evergreen: '#62BE46', breakout: '#006BFF'}

export default {
  name: 'AdRankScatterChart',
  components: {
    DataRenderer,
    AdRankIcon,
    ArrowIcon,
    TrophyIcon
  },
  props: {
    // Ranked ads returned by the brand rankings top-overview request
    scatterAds: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      axisScales: [],
      daysLiveLabels: [],
      rankLabels: ['#1', '#20', '#40', '#60', '#80', '#100'],
      dataAreaDimensions: { width: 0, height: 0 },
      // colorReferences: [
      //   { label: 'Stable', color: CATEGORY_COLORS.stable },
      //   { label: 'Climbing', color: CATEGORY_COLORS.climbing },
      //   { label: 'Declining', color: CATEGORY_COLORS.declining }
      // ],
      resizeObserver: null,

      loadingPointDetailsId: null,
    }
  },
  computed: {
    noData () {
      return !this.loading && !this.scatterAds?.length
    },
    graphData () {
      if (!this.scatterAds?.length) return []
      return this.scatterAds.map(ad => {
        const { category } = ad
        return {
          ...ad,
          pointColor: CATEGORY_COLORS?.[category] ?? '#C0C7D1',
          renderOnTop: category === 'evergreen'
        }
      })
    },
  },
  watch: {
    scatterAds: {
      handler () {
        this.recomputeChartRenderData()
      },
      deep: true
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.initializeAxisInformation()
      this.computeDataAreaDimensions()
      if (this.$refs.graphContainer) {
        this.resizeObserver = new ResizeObserver(() => {
          this.computeDataAreaDimensions()
          this.initializeAxisInformation()
          this.$refs.dataRenderer.computeDataPoints()
        })
        this.resizeObserver.observe(this.$refs.graphContainer)
      }
    })
  },
  beforeDestroy () {
    if (this.resizeObserver && this.$refs.graphContainer) {
      this.resizeObserver.unobserve(this.$refs.graphContainer)
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  methods: {
    async fetchPointAdvertisement (pointData) {
      try {
        const { results } = await ForeplayAPI.Ads.getUniqueAdsByIds([pointData.id])
        this.$emit('show-ad-details', results[0])
      } catch (error) {
        console.error('Error fetching point advertisement:', error)
        this.$showAlert({
          message: 'Error fetching advertisement. See console for details.',
          type: 'error'
        })
      } finally {
        this.loadingPointDetailsId = null
      }
    },
    recomputeChartRenderData () {
      this.initializeAxisInformation()
      this.$refs.dataRenderer.computeDataPoints()
    },
    initializeAxisInformation () {
      if (!this.scatterAds?.length) {
        this.axisScales = []
        this.daysLiveLabels = []
        return
      }
      this.computeAxisScales()
      this.computeDaysLiveLabels()
    },
    computeAxisScales () {
      const scales = []
      if (this.scatterAds?.length) {
        // Create the scale for days live
        const maxDaysLive = Math.max(...this.scatterAds.map(ad => ad.days_running)) || 1
        const daysLiveScale  = scaleLinear().domain([0, maxDaysLive]).nice()
        scales.push(daysLiveScale)
        // Create the scale for rank
        const rankScale = scaleLinear().domain([100, 1])
        scales.push(rankScale)
      }
      this.axisScales = scales
    },
    computeDaysLiveLabels () {
      this.daysLiveLabels = []
      let maxTicks = 10
      if (this.$refs.graphContainer) {
        const width = this.$refs.graphContainer.clientWidth
        if (!width) return // Don't compute labels if the container is not rendered yet (bit of a hack - resize triggers when we switch tabs due to keep-alive)
        maxTicks = Math.max(Math.floor(width / MIN_X_TICK_SPACING), 1)
      }
      const scale = this.axisScales[0]
      let ticks = scale.ticks(maxTicks)
      ticks = this.reduceLabelTicks(ticks, scale, maxTicks)
      this.daysLiveLabels = ticks.map(tick => `${tick}D`)
    },
    reduceLabelTicks (ticks, scale, maxTicks) {
      if (ticks.length <= maxTicks) return ticks
      let tickValues = [...ticks]
      if (ticks.length % 2 === 0) {
        const newMax = ticks[ticks.length - 1] + ticks[1]
        tickValues.push(newMax)
        scale.domain([0, newMax])
      }
      while (tickValues.length > maxTicks) {
        tickValues = tickValues.filter((_, index) => index % 2 === 0)
      }
      return tickValues
    },
    computeDataAreaDimensions () {
      if (!this.$refs.dataArea) return
      const areaBounds = this.$refs.dataArea
      this.dataAreaDimensions = {
        width: areaBounds.clientWidth,
        height: areaBounds.clientHeight
      }
    },
    getCategoryLabel (category) {
      switch (category) {
        case 'breakout':
          return {
            label: 'Breakout',
            icon: BreakoutIcon,
            color: { icon: '#84BDF6', text: '#C0E4FF', background: 'rgba(0, 107, 255, 0.12)'}
          }
        case 'evergreen':
          return {
            label: 'Evergreen',
            icon: EvergreenIcon,
            color: { icon: '#E0F2D4', text: '#DCF3D1', background: 'rgba(97, 190, 70, 0.12)'}
          }
        default: return null
      }
    },
  }
}
</script>

<style scoped>
.scatter-background {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle at 1px 1px, rgba(235, 239, 243, 0.75) 1px, transparent 0);
  /* background-image: radial-gradient(circle at 1px 1px, red 1px, transparent 0); */
  background-size: 8px 8px;
  background-position: 0 0;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 100ms ease-in-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>